import App from './app';
import AxiosInterceptor from './plugins/AxiosInterceptor';
import { CactusAuthenticator } from '@cactus-web/authenticator';
import CactusGraphQlClient from './plugins/cactusGraphql';
import ErrorHandler from './plugins/errorHandler';
import LoadingSpinner from './plugins/loadingSpinner';
import VeeValidate from 'vee-validate';
import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
import VueI18n from 'vue-i18n';
import VueLogger from 'vuejs-logger';
import bugsnag from '@bugsnag/js';
import bugsnagVue from '@bugsnag/plugin-vue';
import formatValueWithUnit from './helpers/filters/formatValueWithUnit';
import getMetaData from './utils/redirecter';
import returnValueWithPercentage from './helpers/filters/returnPercentageValue';
import returnRoundedWithUnit from './helpers/filters/returnRoundedWithUnit';
import router from './router';
import store from './store';
import translations from './i18n';
import Moment from 'moment-timezone';
import 'moment/locale/de';
import DevHelper from './plugins/devHelper';
import Toasted from 'vue-toasted';

const sanitizeHtml = require('sanitize-html');
Vue.prototype.$sanitize = sanitizeHtml;

/**
 * Polyfil as IE doesn't support Promise.finally()
 */
Promise.prototype.finally = function(cb) {
  const res = () => this;
  const fin = () => Promise.resolve(cb()).then(res);
  return this.then(fin, fin);
};

Vue.config.productionTip = false;
const isProduction = process.env.NODE_ENV === 'production';
const packageJson = require('../package.json');

/*
 * The very first step is to retrieve metadata from the redirecter. Only when
 * this information is provided, the bootstrapping of the app can happen. Otherwise
 * the app won't be usable.
 */
getMetaData(
  'https://redirecter.cactus.sdi.tools/metadata/system.js?x-cactus-client=p3web&version=v2'
)
  .then(data => {
    Vue.config.metadata = data || {};

    /**
     * CactusAuthenticator needs certain cognito data. Without it, it won't work
     */
    Vue.use(CactusAuthenticator, {
      awsConfig: {
        identityPoolId: data.cognito.identity_pool_id,
        region: data.region,
        userPoolId: data.cognito.user_pool_id,
        userPoolWebClientId: data.cognito.clients.id
      }
    });

    /**
     * Pass current token into AxiosInterceptor
     */
    Vue.use(AxiosInterceptor, {
      getSession: Vue.prototype.$getCurrentSession
    });

    Vue.use(CactusGraphQlClient, {
      getSession: Vue.prototype.$getCurrentSession,
      apiUrl: `${Vue.config.metadata.apis.data}/graphql`
    });

    /**
     * All required data for bugsnag will be delivered by the redirecter
     */
    const bugsnagClient = bugsnag({
      apiKey: data.bugsnag.api_key,
      appVersion: packageJson.version || 'n/a',
      releaseStage: data.stage,
      notifyReleaseStages: ['int', 'qa', 'prod']
    });

    if (data.bugsnag) bugsnagClient.use(bugsnagVue, Vue);

    /**
     * Configuration of central error handler. reportBug is a callback
     * function, which will forward all errors explicitly to bugsnag.
     */
    Vue.use(ErrorHandler, {
      reportBug: errorObj => bugsnagClient.notify(errorObj)
    });

    /**
     * Plugin to handle form validation
     */
    Vue.use(VeeValidate);

    /***
     * Plugin for displaying toast messages
     */
    Vue.use(Toasted);
    const toastedConfig = {
      singleton: true,
      duration: 1000 * 7,
      position: 'bottom-center',
      fullWidth: true
    };
    Vue.toasted.register('jlToast', payload => payload.message || '', toastedConfig);

    Vue.toasted.register('jlToastError', payload => payload.message || '', {
      ...toastedConfig,
      containerClass: 'jlToast--error'
    });

    /**
     * Plugin to register gesture callbacks
     */
    Vue.use(Vue2TouchEvents, {
      disableClick: false,
      touchClass: 'touch',
      tapTolerance: 10,
      swipeTolerance: 30
    });

    /**
     * Configuration of i18n an l10n. For now, we use german as default.
     */
    const numberFormats = {
      de: {
        noDecimals: {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        },
        withDecimals: {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        },
        currency: {
          style: 'currency',
          currency: 'EUR'
        }
      },
      en: {
        noDecimals: {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        },
        withDecimals: {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        },
        currency: {
          style: 'currency',
          currency: 'EUR'
        }
      }
    };
    Vue.use(VueI18n);
    const translationConfig = {
      locale: 'de',
      messages: translations,
      numberFormats
    };

    /**
     * Set default locale of moment.js to german format.
     * We have to manually set the timezone, otherwise charts will
     * render strangely. See Jira JL-133
     */
    // Moment.locale('de');
    Vue.prototype.$moment = Moment;
    // TODO Default timezone can be remove #### NEEDS CHARTS REVIEW ####
    Vue.prototype.$moment.locale('de');
    Vue.prototype.$moment.tz.setDefault('Europe/Berlin', true);

    /**
     * 14/10/2020
     * Change week day start to monday, in order to keep the charts with the same values between de/en
     * 0 = sunday, 1 = monday (...) 6 = saturday
     */
    Vue.prototype.$moment.updateLocale('en', {
      week: {
        dow: 1 // First day of week is Monday
      }
    });

    // TODO: this filter might have to be moved into a different location
    Vue.filter('kWh', function(value) {
      return formatValueWithUnit('kWh', value);
    });
    Vue.filter('percentage', function(value) {
      return returnValueWithPercentage('%', value);
    });
    Vue.filter('roundedKWh', function(value) {
      return returnRoundedWithUnit('kWh', value);
    });

    /**
     * Configuration of vue logger. On production we only
     * want to output level `error` on the console
     */
    Vue.use(VueLogger, {
      isEnabled: true,
      logLevel: isProduction ? 'error' : 'debug',
      stringifyArguments: false,
      showLogLevel: true,
      showMethodName: true,
      separator: '|',
      showConsoleColors: true
    });

    /**
     * Custom plugin for showing/hiding a global loading spinner
     */
    Vue.use(LoadingSpinner, {
      loggingFn: Vue.prototype.$log.debug
    });

    Vue.use(DevHelper);

    new Vue({
      store,
      router,
      i18n: new VueI18n(translationConfig),
      render: h => h(App)
    }).$mount('#app');
  })
  .catch(err => {
    console.log('Could not retrieve app configuration', err);
  });
